"use client";

import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { cn } from "@/lib/utils";
import { ArrowRight, LoaderPinwheel } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { PRIZE_IMAGE_MAP } from "./PrizeWheelUtils";

interface PrizeWheelMenuItemProps {
  className?: string;
  walletAddress?: string;
  spinsRemaining?: number;
  prizeName?: string;
  tier?: number;
  isTransitioning?: boolean;
  onClick?: () => void;
}

export function PrizeWheelMenuItem({
  className,
  walletAddress = "0x235...235",
  spinsRemaining = 0,
  prizeName = "10 IMX",
  tier = 5,
  isTransitioning = false,
  onClick,
}: PrizeWheelMenuItemProps) {
  const { trackV3 } = useAnalytics();
  const { isLoggedIn } = usePassportProvider();
  const [isHovered, setIsHovered] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const [confettiOpacity, setConfettiOpacity] = useState(1);
  const prevTransitioningRef = useRef(isTransitioning);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Show confetti for legendary prizes (tier 1 or 2)
  const showConfetti = tier === 1 || tier === 2 || tier === 3;

  // Reset and play confetti when prize changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: We need prizeName and walletAddress to detect when prize changes
  useEffect(() => {
    if (showConfetti && videoRef.current) {
      setConfettiOpacity(1);
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  }, [showConfetti, prizeName, walletAddress]);

  // Handle video end - fade out
  const handleVideoEnd = () => {
    setConfettiOpacity(0);
  };

  // When isTransitioning goes from true to false during render, set entering state
  const transitionEnded = prevTransitioningRef.current && !isTransitioning;
  if (transitionEnded && !isEntering) {
    // Transition just ended, set entering state synchronously during render
    setIsEntering(true);
  }

  // Clear entering state after delay
  useEffect(() => {
    if (isEntering) {
      const timer = setTimeout(() => setIsEntering(false), 50);
      return () => clearTimeout(timer);
    }
  }, [isEntering]);

  // Update transition ref after render
  useEffect(() => {
    prevTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  // Map the prize name to the image key (case-insensitive)
  const getPrizeImageKey = (name: string): keyof typeof PRIZE_IMAGE_MAP => {
    const normalizedName = name.toLowerCase();

    // Try exact match first
    if (normalizedName in PRIZE_IMAGE_MAP) {
      return normalizedName as keyof typeof PRIZE_IMAGE_MAP;
    }

    // Try partial matches for common variations
    if (normalizedName.includes("meta quest")) return "meta quest 3";
    if (normalizedName.includes("playstation")) return "playstation 5";
    if (normalizedName.includes("series x")) return "series x";
    if (normalizedName.includes("switch")) return "switch 2";
    if (normalizedName.includes("steam")) return "$20 steam";
    if (normalizedName.includes("xbox") && normalizedName.includes("card"))
      return "$25 xbox";
    if (
      normalizedName.includes("playstation") &&
      normalizedName.includes("card")
    )
      return "$20 playstation";
    if (normalizedName.includes("apple")) return "$20 apple";

    // Handle IMX amounts
    if (normalizedName.includes("50 imx")) return "50 imx";
    if (normalizedName.includes("10 imx")) return "10 imx";
    if (normalizedName.includes("5 imx")) return "5 imx";
    if (normalizedName.includes("1 imx")) return "1 imx";

    // Handle gem amounts
    if (normalizedName.includes("500 gems")) return "500 gems";
    if (normalizedName.includes("100 gems")) return "100 gems";
    if (normalizedName.includes("50 gems")) return "50 gems";
    if (normalizedName.includes("20 gems")) return "20 gems";
    if (normalizedName.includes("10 gems")) return "10 gems";
    if (normalizedName.includes("5 gems")) return "5 gems";

    // Default fallback
    return "5 gems";
  };

  const prizeImageKey = getPrizeImageKey(prizeName);

  const handleClick = () => {
    trackV3({
      object: "Prize_Wheel_Menu_Item",
      action: Action.PRESSED,
      extras: {
        spinsRemaining,
        walletAddress,
        isLoggedIn: Boolean(isLoggedIn),
      },
    });
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      className={cn(
        "relative overflow-hidden rounded-lg cursor-pointer transition-all duration-200 w-full text-left",
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Confetti video overlay for legendary prizes - blends with content */}
      {showConfetti && (
        <>
          {/* Video layer with blend mode */}
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-full h-full object-cover z-[5] pointer-events-none transition-opacity duration-1000"
            style={{
              mixBlendMode: "screen",
              opacity: confettiOpacity * 0.6,
            }}
            muted
            playsInline
            onEnded={handleVideoEnd}
          >
            <source src="/assets/prize-wheel/confetti.mp4" type="video/mp4" />
          </video>

          {/* Overlay with blend mode */}
          <div
            className="absolute top-0 left-0 w-full h-full z-[6] pointer-events-none transition-opacity duration-1000"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,105,180,0.2))",
              mixBlendMode: "overlay",
              opacity: confettiOpacity,
            }}
          />
        </>
      )}

      {/* Main content background with blur */}
      <div className="absolute inset-0 md:backdrop-blur-xl bg-transparent md:bg-white/5 z-[1]" />

      {/* Prize wheel decorative image - above blur */}
      <div className="absolute bottom-[-134px] right-[-29px] w-[199px] h-[199px] z-[2] pointer-events-none">
        <div className="relative w-full h-full">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[199px] h-[199px] rotate-[148deg]">
              <Image
                src="/assets/prize-wheel/wheel-small.webp"
                alt=""
                width={199}
                height={199}
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Glassy gradient border - pixel perfect with high contrast - thickens on hover - always on top */}
      <div className="absolute inset-0 z-[4] pointer-events-none rounded-lg">
        {/* Gradient border layer */}
        <div
          className="w-full h-full rounded-lg absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at top left, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.15) 40%, transparent 60%),
              radial-gradient(circle at top right, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.08) 40%, transparent 60%),
              radial-gradient(circle at bottom left, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.08) 40%, transparent 60%),
              radial-gradient(circle at bottom right, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.15) 40%, transparent 60%),
              rgba(255,255,255,0.04)
            `,
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            padding: "1px",
            opacity: isHovered ? 0 : 1,
            transition: "opacity 300ms ease-in-out",
          }}
        />

        {/* Solid white border layer for hover */}
        <div
          className="w-full h-full rounded-lg absolute inset-0"
          style={{
            background: "rgba(255,255,255,0.75)",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            padding: "1.5px",
            opacity: isHovered ? 1 : 0,
            transition: "opacity 300ms ease-in-out",
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative px-3 py-3 z-[3]">
        <div className="flex flex-col gap-3 relative">
          {/* Header - Free Spins Remaining */}
          <div className="flex flex-col gap-1">
            <div className="flex gap-2.5 items-start w-full">
              <LoaderPinwheel className="w-3.5 h-3.5 shrink-0 text-neutral-50" />
              <div className="flex flex-col gap-1 grow min-w-0">
                <div className="flex gap-2.5 items-center w-full">
                  <div className="flex items-center grow min-w-0 h-3.5">
                    <p className="font-suisse font-medium text-sm leading-none text-neutral-50 grow min-w-0">
                      FREE Spins Remaining
                    </p>
                  </div>
                  <div className="flex items-center h-3.5 shrink-0">
                    <p className="font-suisse font-bold text-sm leading-none text-neutral-50 whitespace-nowrap">
                      {spinsRemaining}
                    </p>
                  </div>
                </div>
                <div className="flex items-center w-full max-w-[280px]">
                  <p className="font-roboto font-normal text-[10px] leading-[14px] tracking-[0.1px] text-[#b6b6b6] grow min-w-0">
                    $5000 in prizes being won each week
                  </p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 shrink-0 text-neutral-50" />
            </div>
          </div>

          {/* Divider */}
          <div className="flex gap-3 h-px items-center opacity-25 w-full">
            <div className="bg-neutral-50 h-px grow min-w-0 opacity-[0.15]" />
          </div>

          {/* Recent Winners Section */}
          <div className="flex flex-col gap-1 w-full">
            <div className="flex flex-col gap-1 justify-center w-full overflow-hidden">
              {/* Section heading */}
              <div className="flex gap-2.5 items-start w-full">
                <div className="opacity-0 w-3.5 shrink-0">
                  <LoaderPinwheel className="w-3.5 h-3.5" />
                </div>
                <div className="flex flex-col gap-1 grow min-w-0">
                  <div className="flex gap-2.5 items-center w-full">
                    <div className="flex gap-2 items-center grow min-w-0">
                      <p className="font-roboto font-semibold text-[10px] leading-normal tracking-[0.1px] text-[#b6b6b6] grow min-w-0">
                        RECENT WINNERS
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Winner content with roll up effect - exits up, enters from bottom */}
              <div
                className="flex gap-2 items-center w-full transition-all duration-500 ease-in-out"
                style={{
                  opacity: isTransitioning ? 0 : isEntering ? 0 : 1,
                  transform: isTransitioning
                    ? "translateY(-24px)"
                    : isEntering
                      ? "translateY(24px)"
                      : "translateY(0)",
                }}
              >
                <div className="flex items-center justify-center w-10 h-10 shrink-0">
                  <div className="w-full h-full overflow-hidden relative">
                    <Image
                      src={PRIZE_IMAGE_MAP[prizeImageKey]}
                      alt="Prize"
                      width={40}
                      height={40}
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1 justify-center w-[282px] shrink-0">
                  <div className="flex flex-col items-start w-full">
                    <div className="flex flex-col gap-2.5 items-end justify-center w-[282px]">
                      <div className="flex gap-2 items-center w-full">
                        <p className="font-roboto font-semibold text-xs leading-[14px] tracking-[0.12px] text-neutral-50 grow min-w-0">
                          {walletAddress}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 h-3.5 items-start w-[282px]">
                      <div className="flex gap-2 h-3.5 items-center">
                        <p className="font-roboto font-normal text-[10px] leading-[14px] tracking-[0.1px] text-[#b6b6b6] whitespace-nowrap">
                          {prizeName} Won
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
